import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { formatDate, getStatusColor, getStatusLabel } from '@/lib/utils'
import toast from 'react-hot-toast'
import { ArrowLeftIcon, StarIcon, NoSymbolIcon, TrashIcon, ArrowPathIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline'

interface User {
  _id: string
  email: string
  name?: string
  avatar?: string
  status: string
  isPremium: boolean
  premiumActivatedAt?: string
  language: string
  progress: {
    currentLevel: number
    totalPoints: number
    currentStreak: number
    longestStreak: number
    completedLessons: string[]
    levelProgress: Array<{
      level: number
      completedLessons: string[]
      quizPassed: boolean
    }>
  }
  analytics?: {
    utmSource?: string
    utmMedium?: string
    utmCampaign?: string
  }
  brokerClickedAt?: string
  brokerRegisteredAt?: string
  createdAt: string
  lastActiveAt?: string
}

export default function UserDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  
  const { data: user, isLoading } = useQuery({
    queryKey: ['user', id],
    queryFn: async () => {
      const res = await api.get<{ data: { user: User } }>(`/admin/users/${id}`)
      return res.data.data.user
    },
  })
  
  const togglePremiumMutation = useMutation({
    mutationFn: async (isPremium: boolean) => {
      await api.post(`/admin/users/${id}/premium`, { isPremium })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', id] })
      toast.success('Статус Premium обновлён')
    },
  })
  
  const blockMutation = useMutation({
    mutationFn: async () => {
      await api.post(`/admin/users/${id}/block`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', id] })
      toast.success('Пользователь заблокирован')
    },
  })
  
  const deleteMutation = useMutation({
    mutationFn: async () => {
      await api.delete(`/admin/users/${id}`)
    },
    onSuccess: () => {
      toast.success('Пользователь удалён')
      navigate('/users')
    },
  })

  const resetPointsMutation = useMutation({
    mutationFn: async () => {
      await api.post(`/admin/users/${id}/reset-points`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', id] })
      toast.success('Баллы пользователя обнулены')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Ошибка сброса баллов')
    },
  })

  const clearChatHistoryMutation = useMutation({
    mutationFn: async () => {
      return await api.post(`/admin/users/${id}/clear-chat-history`)
    },
    onSuccess: (response) => {
      const data = response.data.data
      toast.success(`История ИИ чата очищена: ${data.deletedChats} чатов, ${data.deletedMessages} сообщений`)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Ошибка очистки истории чата')
    },
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Загрузка...</div>
      </div>
    )
  }
  
  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Пользователь не найден</div>
      </div>
    )
  }
  
  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/users')}
            className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-bold text-white">{user.email}</h1>
          <span className={getStatusColor(user.status)}>
            {getStatusLabel(user.status)}
          </span>
          {user.isPremium && (
            <span className="badge-yellow">Premium</span>
          )}
        </div>
        
        <div className="flex gap-2">
          <button
            onClick={() => togglePremiumMutation.mutate(!user.isPremium)}
            className={user.isPremium ? 'btn-secondary' : 'btn-primary'}
          >
            <StarIcon className="w-5 h-5 mr-2" />
            {user.isPremium ? 'Снять Premium' : 'Дать Premium'}
          </button>
          {user.status !== 'blocked' && (
            <button
              onClick={() => blockMutation.mutate()}
              className="btn-secondary"
            >
              <NoSymbolIcon className="w-5 h-5 mr-2" />
              Заблокировать
            </button>
          )}
          <button
            onClick={() => {
              if (confirm('Удалить пользователя? Это действие необратимо.')) {
                deleteMutation.mutate()
              }
            }}
            className="btn-danger"
          >
            <TrashIcon className="w-5 h-5 mr-2" />
            Удалить
          </button>
        </div>
      </div>

      {/* Testing Actions Card */}
      <div className="card bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/30">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-white mb-2">Действия для тестирования</h3>
            <p className="text-sm text-gray-400">Сбросить данные пользователя для тестирования ИИ чата</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => {
                if (confirm('Обнулить баллы и прогресс пользователя?')) {
                  resetPointsMutation.mutate()
                }
              }}
              disabled={resetPointsMutation.isPending}
              className="btn-secondary flex items-center gap-2"
            >
              <ArrowPathIcon className="w-5 h-5" />
              {resetPointsMutation.isPending ? 'Обнуление...' : 'Обнулить баллы'}
            </button>
            <button
              onClick={() => {
                if (confirm('Очистить всю историю ИИ чата?')) {
                  clearChatHistoryMutation.mutate()
                }
              }}
              disabled={clearChatHistoryMutation.isPending}
              className="btn-secondary flex items-center gap-2"
            >
              <ChatBubbleLeftRightIcon className="w-5 h-5" />
              {clearChatHistoryMutation.isPending ? 'Очистка...' : 'Очистить историю ИИ'}
            </button>
          </div>
        </div>
      </div>

      {/* Info cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Basic info */}
        <div className="card">
          <h3 className="text-lg font-medium text-white mb-4">Основная информация</h3>
          <dl className="space-y-3">
            <div>
              <dt className="text-sm text-gray-400">Email</dt>
              <dd className="text-white">{user.email}</dd>
            </div>
            {user.name && (
              <div>
                <dt className="text-sm text-gray-400">Имя</dt>
                <dd className="text-white">{user.name}</dd>
              </div>
            )}
            <div>
              <dt className="text-sm text-gray-400">Язык</dt>
              <dd className="text-white">{user.language === 'ru' ? 'Русский' : 'English'}</dd>
            </div>
            <div>
              <dt className="text-sm text-gray-400">Дата регистрации</dt>
              <dd className="text-white">{formatDate(user.createdAt)}</dd>
            </div>
            {user.lastActiveAt && (
              <div>
                <dt className="text-sm text-gray-400">Последняя активность</dt>
                <dd className="text-white">{formatDate(user.lastActiveAt)}</dd>
              </div>
            )}
          </dl>
        </div>
        
        {/* Progress */}
        <div className="card">
          <h3 className="text-lg font-medium text-white mb-4">Прогресс</h3>
          <dl className="space-y-3">
            <div>
              <dt className="text-sm text-gray-400">Текущий уровень</dt>
              <dd className="text-2xl font-bold text-white">{user.progress.currentLevel}</dd>
            </div>
            <div>
              <dt className="text-sm text-gray-400">Всего баллов</dt>
              <dd className="text-2xl font-bold text-primary-400">{user.progress.totalPoints}</dd>
            </div>
            <div>
              <dt className="text-sm text-gray-400">Текущий streak</dt>
              <dd className="text-white">{user.progress.currentStreak} дней</dd>
            </div>
            <div>
              <dt className="text-sm text-gray-400">Лучший streak</dt>
              <dd className="text-white">{user.progress.longestStreak} дней</dd>
            </div>
            <div>
              <dt className="text-sm text-gray-400">Пройдено уроков</dt>
              <dd className="text-white">{user.progress.completedLessons.length}</dd>
            </div>
          </dl>
        </div>
        
        {/* Premium & Broker */}
        <div className="card">
          <h3 className="text-lg font-medium text-white mb-4">Premium & Брокер</h3>
          <dl className="space-y-3">
            <div>
              <dt className="text-sm text-gray-400">Premium статус</dt>
              <dd className={user.isPremium ? 'text-yellow-400 font-medium' : 'text-gray-400'}>
                {user.isPremium ? 'Активен' : 'Нет'}
              </dd>
            </div>
            {user.premiumActivatedAt && (
              <div>
                <dt className="text-sm text-gray-400">Premium с</dt>
                <dd className="text-white">{formatDate(user.premiumActivatedAt)}</dd>
              </div>
            )}
            <div>
              <dt className="text-sm text-gray-400">Клик на брокера</dt>
              <dd className={user.brokerClickedAt ? 'text-green-400' : 'text-gray-400'}>
                {user.brokerClickedAt ? formatDate(user.brokerClickedAt) : 'Нет'}
              </dd>
            </div>
            <div>
              <dt className="text-sm text-gray-400">Регистрация у брокера</dt>
              <dd className={user.brokerRegisteredAt ? 'text-green-400' : 'text-gray-400'}>
                {user.brokerRegisteredAt ? formatDate(user.brokerRegisteredAt) : 'Нет'}
              </dd>
            </div>
          </dl>
        </div>
      </div>
      
      {/* Level progress */}
      <div className="card">
        <h3 className="text-lg font-medium text-white mb-4">Прогресс по уровням</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-10 gap-4">
          {Array.from({ length: 10 }, (_, i) => i + 1).map((level) => {
            const progress = user.progress.levelProgress.find((lp) => lp.level === level)
            const isCompleted = progress?.quizPassed
            const isInProgress = progress && !progress.quizPassed
            
            return (
              <div
                key={level}
                className={`p-4 rounded-lg text-center ${
                  isCompleted
                    ? 'bg-green-500/20 border border-green-500/50'
                    : isInProgress
                    ? 'bg-yellow-500/20 border border-yellow-500/50'
                    : 'bg-gray-700/50 border border-gray-600'
                }`}
              >
                <div className="text-2xl font-bold text-white">{level}</div>
                <div className="text-xs text-gray-400 mt-1">
                  {isCompleted ? 'Завершён' : isInProgress ? 'В процессе' : 'Не начат'}
                </div>
              </div>
            )
          })}
        </div>
      </div>
      
      {/* Analytics */}
      {user.analytics && (user.analytics.utmSource || user.analytics.utmMedium || user.analytics.utmCampaign) && (
        <div className="card">
          <h3 className="text-lg font-medium text-white mb-4">Аналитика</h3>
          <dl className="grid grid-cols-3 gap-4">
            {user.analytics.utmSource && (
              <div>
                <dt className="text-sm text-gray-400">UTM Source</dt>
                <dd className="text-white">{user.analytics.utmSource}</dd>
              </div>
            )}
            {user.analytics.utmMedium && (
              <div>
                <dt className="text-sm text-gray-400">UTM Medium</dt>
                <dd className="text-white">{user.analytics.utmMedium}</dd>
              </div>
            )}
            {user.analytics.utmCampaign && (
              <div>
                <dt className="text-sm text-gray-400">UTM Campaign</dt>
                <dd className="text-white">{user.analytics.utmCampaign}</dd>
              </div>
            )}
          </dl>
        </div>
      )}
    </div>
  )
}
